import { ExportService } from "@app/exports";
import { ExportNextClosestProviderActionEnum, ExportPciCrossProjectRequestModel, VolumeServicingProvidersRequestModel, VolumeAdequacySpecialtySummariesRequestModel, VolumeSummariesRequestModel, VolumeZipSummariesRequestModel, ExportAdequacyVolumeAggregateRequestModel, ExportCCIIOProviderModel, ExportCCIIODataValidationsModel } from "@app/exports/exportModels";
import { LoadingSpinner, Page, PageHeader, PageHeaderRight, PageInner } from "@app/shared";
import { NotFound } from "@app/shared/ErrorPages";
import { QESAlert, QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import React, { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, RouteComponentProps, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ExportFormatType, ProviderDropdownTypes, AdequacyAggregateType, AdequacyAggregateGranularity, } from "../../exports/enumerations";
import { NetworkAssessmentAnalysisType, ProviderNetworkType } from "../../projects/enumerators";
import { RoleRestricted, Roles, UserContext } from "../../user";
import { countiesFilterRadioOptions, CountiesFilterRadioOptionsType, reportFormatOptions, ReportFormatOptionsType, VolumeReportsWizardContextSteps } from "./import/interfaces";
import { VolumeReportType, VolumeReportsContext, VolumeReportsProvider } from "./import/VolumeReportsContext";
import VolumeReportsConfig from "./steps/VolumeReportsConfig";
import VolumeReportsFilters from "./steps/VolumeReportsFilters";
import VolumeReportsSelectProjects from "./steps/VolumeReportsSelectProjects";
import VolumeReportsSelectServiceArea from "./steps/VolumeReportsSelectServiceArea";
import VolumeReportsSelectSpecialties from "./steps/VolumeReportsSelectSpecialties";
import { compact } from "lodash";
import { focusFirstInteractiveElement } from "@app/shared/utility";
import { FeatureFlagContext, Features } from "../../feature-flags";

const VolumeReportsWizard = (props: RouteComponentProps): JSX.Element => {
  const { reportType } = useParams<{ reportType: VolumeReportType }>();
  const user = useContext(UserContext);

  if (!["providers", "specialty", "pci", "zip-summary", "countyaggregate", "stateaggregate", "specialtyaggregate", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(reportType) || (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(reportType) && !user.roles.includes(Roles.VOLUME_REPORTER))) {
    return <NotFound />;
  }
  return (
    <VolumeReportsProvider reportType={reportType}>
      <VolumeReportsWizardContent {...props} />
    </VolumeReportsProvider>
  );
};

const VolumeReportsWizardContent = (props: RouteComponentProps): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);
  const user = useContext(UserContext);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState(false);
  const furthestPage = useRef(wizardContext.reportType !== "specialtyaggregate" ? VolumeReportsWizardContextSteps.SelectServiceArea : VolumeReportsWizardContextSteps.SelectSpecialty);
  const exportService = useRef(new ExportService()).current;
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  useEffect(() => {
    if (!wizardContext.loading) {
      focusFirstInteractiveElement(wizardBodyRef);
    }
  }, [wizardContext.page, wizardContext.loading]);

  const hasDashboardFilters = useMemo(() => {
    return Object.values(wizardContext.dashboardFilters).length > 0;
  }, [wizardContext.dashboardFilters]);

  const pageTitle = useRef(() => {
    switch (wizardContext.reportType) {
      case "providers":
        return "Providers Report";
      case "specialty":
        return "Specialty Summary by County Report";
      case "countyaggregate":
        return "County Aggregate Report";
      case "stateaggregate":
        return "State Aggregate Report";
      case "specialtyaggregate":
        return "Specialty Aggregate Report";
      case "zip-summary":
        return "Zip Code Summary Report";
      case "pci":
        return "PCI Cross-Project Report";
      case "cciio":
        return "CCIIO QHP Download"
      case "cciioDataValidationsSummary":
        return "CCIIO Data Validation Summary"
      case "cciioDataValidations":
        return "CCIIO Data Validation"
    }
  }).current();

  const handleUpdatePage = (newPage: number) => {
    if (["countyaggregate", "stateaggregate"].includes(wizardContext.reportType)) {
      if (newPage > 0) {
        newPage++;
      }
    }
    else if (["specialtyaggregate"].includes(wizardContext.reportType)) {
      newPage++;
    }

    const step = Object.entries(VolumeReportsWizardContextSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as VolumeReportsWizardContextSteps;
    wizardContext.setPage(VolumeReportsWizardContextSteps[step] as unknown as VolumeReportsWizardContextSteps);
    wizardContext.setShowChangeWarning(true);
  };

  const getCurrentStep = () => {
    if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
      if (["countyaggregate", "stateaggregate"].includes(wizardContext.reportType)) {
        switch (wizardContext.page) {
          case VolumeReportsWizardContextSteps.SelectServiceArea:
            return 0;
          case VolumeReportsWizardContextSteps.SelectProjects:
            return 1;
          case VolumeReportsWizardContextSteps.Filters:
            return 2;
          case VolumeReportsWizardContextSteps.Configure:
            return 3;
          default:
            return;
        }
      }
      else if (["specialtyaggregate"].includes(wizardContext.reportType)) {
        switch (wizardContext.page) {
          case VolumeReportsWizardContextSteps.SelectSpecialty:
            return 0;
          case VolumeReportsWizardContextSteps.SelectProjects:
            return 1;
          case VolumeReportsWizardContextSteps.Filters:
            return 2;
          case VolumeReportsWizardContextSteps.Configure:
            return 3;
          default:
            return;
        }
      }
      else {
        return wizardContext.page;
      }
    } else if (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
      switch (wizardContext.page) {
        case VolumeReportsWizardContextSteps.SelectProjects:
          return 0;
        case VolumeReportsWizardContextSteps.Configure:
          return 1;
        default:
          return;
      }
    }
  }

  const getHumanReadablePageNames = () => {
    const humanReadablePageNames = {
      [VolumeReportsWizardContextSteps.SelectServiceArea]: !["pci", "cciio", "specialtyaggregate", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) ? "Service Area" : null,
      [VolumeReportsWizardContextSteps.SelectSpecialty]: !["pci", "cciio", "countyaggregate", "stateaggregate", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) ? "Specialties" : null,
      [VolumeReportsWizardContextSteps.SelectProjects]: "Projects",
      [VolumeReportsWizardContextSteps.Filters]: !["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) ? "Filters" : null,
      [VolumeReportsWizardContextSteps.Configure]: "Configure",
    };

    return compact(Object.values(humanReadablePageNames));
  }

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        if (wizardContext.reportType !== "specialtyaggregate") {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
          wizardContext.setShowChangeWarning(true);
        }
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        if (wizardContext.reportType !== "countyaggregate" && wizardContext.reportType !== "stateaggregate") {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectSpecialty);
          wizardContext.setShowChangeWarning(true);
        }
        else {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
          wizardContext.setShowChangeWarning(true);
        }
        break;
      case VolumeReportsWizardContextSteps.Filters:
        wizardContext.setPage(VolumeReportsWizardContextSteps.SelectProjects);
        wizardContext.setShowChangeWarning(true);
        break;
      case VolumeReportsWizardContextSteps.Configure:
        if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
          wizardContext.setPage(VolumeReportsWizardContextSteps.Filters);
        } else {
          wizardContext.setPage(VolumeReportsWizardContextSteps.SelectProjects);
        }
        break;
    }
  };

  const validateAndAdvance = () => {
    let newPage: VolumeReportsWizardContextSteps;
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectServiceArea:
        if (wizardContext.reportType !== "countyaggregate" && wizardContext.reportType !== "stateaggregate") {
          newPage = VolumeReportsWizardContextSteps.SelectSpecialty;
        }
        else {
          newPage = VolumeReportsWizardContextSteps.SelectProjects
        }
        break;
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        newPage = VolumeReportsWizardContextSteps.SelectProjects;
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        if (wizardContext.projects.length > 0) {
          if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType)) {
            newPage = VolumeReportsWizardContextSteps.Filters;
          } else {
            newPage = VolumeReportsWizardContextSteps.Configure;
          }
        }
        break;
      case VolumeReportsWizardContextSteps.Filters:
        newPage = VolumeReportsWizardContextSteps.Configure;
        break;
    }
    if (newPage) {
      if (newPage >= furthestPage.current) {
        // we don't reset configure
        furthestPage.current = Math.min(newPage, VolumeReportsWizardContextSteps.Filters);
        wizardContext.setShowChangeWarning(false);
      }
      wizardContext.setPage(newPage);
    }
  };

  const showNoProjectsAlert = useMemo(() => {
    return wizardContext.page === VolumeReportsWizardContextSteps.SelectProjects &&
      !wizardContext.loading &&
      wizardContext.projects.length === 0;
  }, [wizardContext.projects, wizardContext.page, wizardContext.loading]);

  const currentWizardStep = useMemo(() => {
    switch (wizardContext.page) {
      case VolumeReportsWizardContextSteps.SelectServiceArea:
        return <VolumeReportsSelectServiceArea pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        return <VolumeReportsSelectSpecialties pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.SelectProjects:
        return <VolumeReportsSelectProjects pageIsValid={setPageIsValid} />;
      case VolumeReportsWizardContextSteps.Filters:
        return <VolumeReportsFilters {...props} pageIsValid={setPageIsValid} isStandardizedReport={featureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT)} />;
      case VolumeReportsWizardContextSteps.Configure:
        return <VolumeReportsConfig pageIsValid={setPageIsValid} {...props} />;
    }
  }, [wizardContext.page]);

  const getExportType = (format: ReportFormatOptionsType) => {
    switch (format) {
      case reportFormatOptions[0]:
        return ExportFormatType.CSV
      case reportFormatOptions[1]:
        return ExportFormatType.Pipe
      case reportFormatOptions[2]:
        return ExportFormatType.Tab
    }
  };

  const getNetworkAnalysisTypes = (types: CountiesFilterRadioOptionsType[]) => {
    const networkAnalysisTypes: NetworkAssessmentAnalysisType[] = [];
    types.forEach((type) => networkAnalysisTypes.push(getNetworkAnalysisType(type)));
    return networkAnalysisTypes;
  };

  const getNetworkAnalysisType = (type: CountiesFilterRadioOptionsType) => {
    switch (type) {
      case countiesFilterRadioOptions[0]:
        return NetworkAssessmentAnalysisType.ServiceArea
      case countiesFilterRadioOptions[1]:
        return NetworkAssessmentAnalysisType.Opportunity
    }
  };

  const isHsdFormat = (filter: string) => filter !== "Default Format" && filter !== 'CCIIO PPF Report' && filter !== 'Distinct Servicing Providers';

  const handleGenerate = async () => {
    const columnPresetId = featureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) && wizardContext.selectedColumnPreset.id > 0 ? wizardContext.selectedColumnPreset.id : null;
    switch (wizardContext.reportType) {
      case "providers":
        const isNcp = wizardContext.providerType.toString() === ProviderDropdownTypes.NextClosestProviders.toString();
        /* eslint-disable */
        const providersRequestModel: VolumeServicingProvidersRequestModel = {
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          exportName: wizardContext.reportName,
          publishedOnly: wizardContext.publishedOnly,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(wizardContext.servicingType)],
          providerNetworkTypes: isNcp ? [ProviderNetworkType.Network] : [wizardContext.providerType],
          hsdFormat: isHsdFormat(wizardContext.reportFilter),
          hsdColumns: undefined,
          includeCargoColumns: wizardContext.includeCargoColumns,
          cargoColumnsToInclude: undefined,
          networkProviderFilterType: wizardContext.networkProviderFilterType,
          impactProviderType: wizardContext.impactProviderType,
          nextClosestProviderAction: isNcp ? ExportNextClosestProviderActionEnum.FilterToNextClosestProviderOnly : wizardContext.appendNcp ? ExportNextClosestProviderActionEnum.AppendNextClosestProviders : ExportNextClosestProviderActionEnum.None,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          allowParallelization: true,
          separateReportOutput: wizardContext.splitReport,
          generateNoDataFoundReadme: true,
          formatType: getExportType(wizardContext.reportFormat),
          eMailOnSuccess: wizardContext.alertWhenReady,
          runAsynchronous: true,
          IncludeActiveRegionSets: wizardContext.activeCounties,
          IncludePendingRegionSets: wizardContext.pendingCounties,
          IncludeApprovedRegionSets: wizardContext.approvedCounties,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          IncludeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          IncludePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
          sharedUsers: wizardContext.sharedUsers,
          isCCIIOPPFReport: wizardContext.reportFilter === 'CCIIO PPF Report',
          isDistinctServicingProviderReport: wizardContext.reportFilter === 'Distinct Servicing Providers',
          appendPatternOfCareProviders: wizardContext.appendPatternOfCareProviders && featureFlags.has(Features.MANAGE_HSD_PROVIDERS) && user.roles.includes(Roles.PATTERN_OF_CARE_USER),
          suppressHSDColumns: wizardContext.projects.length > 1 && wizardContext.reportFilter === "MA HSD Format" && !wizardContext.splitReport,
          reportCustomizationId: columnPresetId,
        };
        await exportService.postVolumeServicingProviders(providersRequestModel);
        break;
      case "specialty":
        // aka summariesRequest
        if (featureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT)) {
          const volumeSpecialtySummariesRequestModel: VolumeAdequacySpecialtySummariesRequestModel = {
            customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
            exportName: wizardContext.reportName,
            networkAssessmentAnalysisTypes: getNetworkAnalysisTypes(wizardContext.specialtySummaryServicingTypes),
            allowParallelization: true,
            includeActiveRegionSets: wizardContext.activeCounties,
            includePendingRegionSets: wizardContext.pendingCounties,
            includeApprovedRegionSets: wizardContext.approvedCounties,
            regionSetGapFilter: wizardContext.countiesGapsFilter,
            regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
            specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
            specialtyGapFilter: wizardContext.specialtyGapFilter,
            separateReportOutput: wizardContext.splitReport,
            publishedOnly: wizardContext.publishedOnly,
            generateNoDataFoundReadme: true,
            formatType: getExportType(wizardContext.reportFormat),
            eMailOnSuccess: wizardContext.alertWhenReady,
            runAsynchronous: true,
            isVolumeReport: true,
            sharedUsers: wizardContext.sharedUsers,
            showExceptionComment: undefined,
            membershipType: wizardContext.membershipTypeFilter,
            reportCustomizationId: columnPresetId,
          };
          await exportService.postVolumeSpecialtySummaries(volumeSpecialtySummariesRequestModel);
        }
        else {
          const specialtyRequestModel: VolumeSummariesRequestModel = {
            customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
            exportName: wizardContext.reportName,
            networkAssessmentAnalysisType: getNetworkAnalysisType(wizardContext.servicingType),
            includeExceptionComment: undefined,
            allowParallelization: true,
            includeActiveRegionSets: wizardContext.activeCounties,
            includePendingRegionSets: wizardContext.pendingCounties,
            includeApprovedRegionSets: wizardContext.approvedCounties,
            regionSetGapFilter: wizardContext.countiesGapsFilter,
            regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
            specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
            specialtyGapFilter: wizardContext.specialtyGapFilter,
            separateReportOutput: wizardContext.splitReport,
            publishedOnly: wizardContext.publishedOnly,
            generateNoDataFoundReadme: true,
            formatType: getExportType(wizardContext.reportFormat),
            eMailOnSuccess: wizardContext.alertWhenReady,
            runAsynchronous: true,
            isVolumeReport: true,
            sharedUsers: wizardContext.sharedUsers,
            reportCustomizationId: columnPresetId,
          };
          await exportService.postVolumeSummaries(specialtyRequestModel);
        }
        break;
      case "zip-summary":
        const zipSummariesRequestModel: VolumeZipSummariesRequestModel = {
          allowParallelization: true,
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          generateNoDataFoundReadme: true,
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includeFacilitySpecialties: wizardContext.includeFacilitySpecialties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includePhysicianSpecialties: wizardContext.includePhysicianSpecialties,
          networkAssessmentAnalysisType: getNetworkAnalysisType(wizardContext.servicingType),
          publishedOnly: wizardContext.publishedOnly,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          regionSetIds: wizardContext.allServiceAreas ? [] : wizardContext.serviceAreas.flatMap(s => s.childServiceAreas.filter((sp) => sp.isChecked).map(c => c.serviceAreaValue)),
          runAsynchronous: true,
          separateReportOutput: wizardContext.splitReport,
          sharedUsers: wizardContext.sharedUsers,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          specialtyGroupCodes: wizardContext.allSpecialties ? [] : wizardContext.specialties.filter((sp) => sp.isChecked).map((sp) => sp.specialtyGroupCode),
          reportCustomizationId: columnPresetId,
        }
        await exportService.postVolumeZipSummaries(zipSummariesRequestModel);

        break;
      case "stateaggregate":
        const stateVolumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          allowParallelization: true,
          generateNoDataFoundReadme: true,
          publishedOnly: wizardContext.publishedOnly,
          separateReportOutput: wizardContext.splitReport,
          isVolumeReport: true,
          aggregateType: AdequacyAggregateType.RegionSet,
          aggregateGranularity: AdequacyAggregateGranularity.State,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          regionSetGapFilter: wizardContext.statesGapsFilter,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          regionSetIds: wizardContext.allServiceAreas
            ? []
            : wizardContext.serviceAreas.flatMap((s) =>
              s.childServiceAreas
                .filter((sp) => sp.isChecked)
                .map((c) => c.serviceAreaValue),
            ),
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          formatType: getExportType(wizardContext.reportFormat),
          exportName: wizardContext.reportName,
          eMailOnSuccess: wizardContext.alertWhenReady,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          reportCustomizationId: columnPresetId,
        };
        await exportService.postVolumeAdequacyAggregates(
          stateVolumeAggregateRequestModel,
        );
        break;
      case "countyaggregate":
        const volumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          allowParallelization: true,
          generateNoDataFoundReadme: true,
          publishedOnly: wizardContext.publishedOnly,
          separateReportOutput: wizardContext.splitReport,
          isVolumeReport: true,
          aggregateType: AdequacyAggregateType.RegionSet,
          aggregateGranularity: AdequacyAggregateGranularity.County,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          regionSetIds: wizardContext.allServiceAreas
            ? []
            : wizardContext.serviceAreas.flatMap((s) =>
              s.childServiceAreas
                .filter((sp) => sp.isChecked)
                .map((c) => c.serviceAreaValue),
            ),
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          formatType: getExportType(wizardContext.reportFormat),
          exportName: wizardContext.reportName,
          eMailOnSuccess: wizardContext.alertWhenReady,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          reportCustomizationId: columnPresetId,
        };
        await exportService.postVolumeAdequacyAggregates(
          volumeAggregateRequestModel,
        );
        break;
      case "specialtyaggregate":
        const specialtyVolumeAggregateRequestModel: ExportAdequacyVolumeAggregateRequestModel =
        {
          customProjectIds: wizardContext.projects
            .filter((sp) => sp.isChecked)
            .map((project) => project.customProjectId),
          allowParallelization: true,
          generateNoDataFoundReadme: true,
          publishedOnly: wizardContext.publishedOnly,
          separateReportOutput: wizardContext.splitReport,
          isVolumeReport: true,
          aggregateType: AdequacyAggregateType.SpecialtyGroup,
          aggregateGranularity: AdequacyAggregateGranularity.ServiceArea,
          networkAssessmentAnalysisTypes: [getNetworkAnalysisType(
            wizardContext.servicingType,
          )],
          includeActiveRegionSets: wizardContext.activeCounties,
          includeApprovedRegionSets: wizardContext.approvedCounties,
          includePendingRegionSets: wizardContext.pendingCounties,
          includeFacilitySpecialties:
            wizardContext.includeFacilitySpecialties,
          includePhysicianSpecialties:
            wizardContext.includePhysicianSpecialties,
          regionSetGapFilter: wizardContext.countiesGapsFilter,
          specialtyGapFilter: wizardContext.specialtyGapFilter,
          regionSetIds: [],
          specialtyGroupCodes: wizardContext.allSpecialties
            ? []
            : wizardContext.specialties
              .filter((sp) => sp.isChecked)
              .map((sp) => sp.specialtyGroupCode),

          //export base model props
          formatType: getExportType(wizardContext.reportFormat),
          exportName: wizardContext.reportName,
          eMailOnSuccess: wizardContext.alertWhenReady,
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          reportCustomizationId: columnPresetId,
        };
        await exportService.postVolumeAdequacyAggregates(
          specialtyVolumeAggregateRequestModel,
        );
        break;
      case "pci":
        const exportPciCrossProjectRequestModel: ExportPciCrossProjectRequestModel = {
          customProjectIds: wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.customProjectId),
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          separateReportOutput: wizardContext.splitReport,
        };

        await exportService.postExportPciCrossProject(exportPciCrossProjectRequestModel);
        break;
      case "cciio":
        const exportCciioProviderModel: ExportCCIIOProviderModel = {
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          includeInvalidCCIIORecords: featureFlags.has(Features.CCIIO_PROVIDER_VALIDATIONS) ? wizardContext.includeInvalidCCIIORecords : true,
          providerSetIds: [...new Set(wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.networkId))],
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
        }
        await exportService.postExportCCIIOProviders(exportCciioProviderModel);
        break;
      case "cciioDataValidationsSummary":
        const exportCciioDataValidationsSummaryModel: ExportCCIIODataValidationsModel = {
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          providerSetIds: [...new Set(wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.networkId))],
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          separateReportOutput: wizardContext.splitReport
        }
        await exportService.postExportCCIIOProviderValidationsSummary(exportCciioDataValidationsSummaryModel);
        break;
      case "cciioDataValidations":
        const exportCciioDataValidationsModel: ExportCCIIODataValidationsModel = {
          eMailOnSuccess: wizardContext.alertWhenReady,
          exportName: wizardContext.reportName,
          formatType: getExportType(wizardContext.reportFormat),
          providerSetIds: [...new Set(wizardContext.projects.filter((sp) => sp.isChecked).map((project) => project.networkId))],
          runAsynchronous: true,
          sharedUsers: wizardContext.sharedUsers,
          separateReportOutput: wizardContext.splitReport
        }
        await exportService.postExportCCIIOProviderValidations(exportCciioDataValidationsModel);
        break;
      default:
        toast("This functionality is not available yet.",
          {
            containerId: "z-index-1030",
            type: "error",
          }
        );
    }
    setPageIsValid(false);
    toast.info('Report Submitted Successfully',
      {
        containerId: "z-index-1030",
        type: "success",
      }
    );
  };

  return (
    <Page documentTitle={`Volume ${pageTitle}`}>
      <PageInner className="vr__wizard">
        <PageHeader title={pageTitle} disableHR={true}>
          <PageHeaderRight>
            <div className="mt-3">
              {wizardContext.reportType !== "pci" && (
                <React.Fragment>
                  <RoleRestricted allowedRoles={[Roles.VIEWER, Roles.RESOURCE_ADMIN]}>
                    <QESButton
                      qesType="secondary"
                      qesTestId="volume-reports__published_results_only"
                      onClick={() => { wizardContext.showPublishedResultsOnly(); furthestPage.current = VolumeReportsWizardContextSteps.Filters; }}
                    >
                      {wizardContext.publishedOnly ? "Include Unpublished Results" : "Published Results Only"}
                    </QESButton>
                  </RoleRestricted>
                  {hasDashboardFilters && (
                    <QESButton
                      qesType="secondary"
                      qesTestId="volume-reports__apply-dashboard-filters"
                      onClick={() => { wizardContext.applyDashboardFilters(); furthestPage.current = VolumeReportsWizardContextSteps.Filters; }}
                    >
                      Apply Project Dashboard Filters
                    </QESButton>
                  )}
                </React.Fragment>
              )}
              <Link to='/reports' className="qes-button qes-button-primary" >Reports</Link>
            </div>
          </PageHeaderRight>
        </PageHeader>
        <WizardSteps currentStep={getCurrentStep()} steps={getHumanReadablePageNames()} setStep={handleUpdatePage} />
        <hr />
        <div ref={wizardBodyRef}>
          {wizardContext.showChangeWarning && !showNoProjectsAlert && (
            <QESAlert type="warning" persist={true} classNames="mt-3">Changing anything on this page will reset later steps.</QESAlert>
          )}
          {showNoProjectsAlert && (
            <QESAlert type="danger" persist={true} classNames="mt-3">
              The applied dashboard filters did not find any projects with successful adequacy results, so no reports can be made.
            </QESAlert>
          )}
          {wizardContext.error && (
            <QESAlert type="danger" persist={true} classNames="mt-3">
              {wizardContext.error}
            </QESAlert>
          )}
          {wizardContext.loading ?
            !!wizardContext.error ?
              null :
              <LoadingSpinner /> :
            currentWizardStep}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/reports" className="qes-button qes-button-secondary danger" data-testid="vr__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="volume-reports__nav-buttons">
            {
              (((!["pci", "cciio", "specialtyaggregate", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectServiceArea)
                || (["specialtyaggregate"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectSpecialty)) ||
                (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && wizardContext.page !== VolumeReportsWizardContextSteps.SelectProjects)) &&
              <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="volume-reports__previous">Previous</QESButton>}
            {wizardContext.page === VolumeReportsWizardContextSteps.Configure ? (
              <QESButton disabled={!pageIsValid} qesType="primary" qesTestId="volume-reports__generate" className="ml-3" onClick={handleGenerate}>
                Generate
              </QESButton>
            ) : (
              <QESButton onClick={validateAndAdvance} disabled={!pageIsValid} qesTestId="volume-reports__next" className="ml-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default VolumeReportsWizard;
